import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_USER_SERVICE_URL;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logOut, setLogOut] = useState(false);

  /* ---------------- HYDRATE AUTH ON LOAD ---------------- */
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const expiresAt = Number(localStorage.getItem("authExpiresAt"));
    const userId = localStorage.getItem("authUserId");
    const email = localStorage.getItem("authEmail");
    const fullName = localStorage.getItem("authFullName");
    const roles = localStorage.getItem("authRoles");

    if (token && userId && expiresAt && Date.now() < expiresAt) {
      setAuth({
        token,
        userId,
        email,
        fullName,
        roles: roles ? roles.split(",") : [],
        expiresAt,
      });
    } else {
      localStorage.clear();
    }

    setLoading(false);
  }, []);

  /* ---------------- LOGOUT (GLOBAL, STABLE) ---------------- */
  const logout = useCallback(
    async (callBackend = true) => {
      if (logOut) return;

      setLogOut(true);

      const token = localStorage.getItem("authToken");

      if (callBackend && token) {
        try {
          await fetch(`${API_BASE}/users/auth/logout`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        } catch {
          // ignore backend logout failure
        }
      }

      localStorage.clear();
      setAuth(null);
      setLogOut(false);
      navigate("/", { replace: true });
    },
    [logOut, navigate]
  );

  /* ---------------- AUTO LOGOUT WHEN TOKEN EXPIRES ---------------- */
  useEffect(() => {
    if (!auth?.expiresAt) return;

    const timeout = auth.expiresAt - Date.now();

    if (timeout <= 0) {
      logout(false);
      return;
    }

    const timer = setTimeout(() => {
      logout(false);
    }, timeout);

    return () => clearTimeout(timer);
  }, [auth?.expiresAt, logout]);

  /* ---------------- LOGIN ---------------- */
  const login = (authData) => {
    const expiresAt = Date.now() + authData.expiresIn * 1000;

    localStorage.setItem("authToken", authData.token);
    localStorage.setItem("authTokenType", authData.tokenType);
    localStorage.setItem("authExpiresAt", expiresAt);
    localStorage.setItem("authUserId", authData.userId);
    localStorage.setItem("authEmail", authData.email);
    localStorage.setItem("authFullName", authData.fullName);
    localStorage.setItem("authRoles", authData.roles.join(","));

    setAuth({
      token: authData.token,
      userId: authData.userId,
      email: authData.email,
      fullName: authData.fullName,
      roles: authData.roles,
      expiresAt,
    });
  };

  /* ---------------- AUTH FETCH (401 + EXPIRY GUARD) ---------------- */
  const authFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem("authToken");
    const expiresAt = Number(localStorage.getItem("authExpiresAt"));

    if (!token || !expiresAt || Date.now() >= expiresAt) {
      await logout(false);
      throw new Error("Session expired");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      await logout(false);
      throw new Error("Unauthorized");
    }

    return response;
  };

  const isAuthenticated = !!auth?.token;

  return (
    <AuthContext.Provider
      value={{
        auth,
        isAuthenticated,
        login,
        logout,
        authFetch,
        loading,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
